import { FC, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';

import { captureError, useDeepCompareMemo } from '@clatter/platform';
import { useTabbedForm } from '../../../../hooks';
import { config } from '../../../../config';
import { addNotification } from '../../../../store';
import { aclRolesMap } from '../../../../constants';
import {
  useGetProjectTemplateByIdQuery,
  useGetFolderAssetsQuery,
  useUpdateDocumentMutation,
  useUpdateProjectMutation,
} from '../../../../services';
import { WorkbookUpload } from '../../../Projects';
import { hasAccessByRole } from '../../../../helpers/acl.helpers';
import { useWorkspace } from '../../../../context/WorkspaceProvider';
import { getLocalStorageAccessToken } from '../../../../helpers/auth';
import { ProjectWorkbookUploadEventBus } from '../../../../events/project-workbook-upload';

import { PageDocumentIcon } from '../../../UI/NgIcons';
import { AddPageDialog, TemplatePage } from './AddPageDialog';
import { StickyHeader } from '../../../UI/StickyHeader/StickyHeader';
import { SchemaForm, NgCard, NgDivider, NgCardContent, StepStatus } from '../../../UI';
import { MicrositesPublishSection } from '../components/MicrositesPublishSection';
import {
  generateInstanceKey,
  generateUniqueDisplayName,
  generateUniqueSlug,
  MicrositePageInstance,
} from '../../../../helpers/micrositeEditor.helpers';
import { MicrositePagesRail, MicrositeTabStatus, MicrositePagesRailTab } from './MicrositePagesRail';

const STICKY_TOP = 65;
const LEFT_COLUMN_WIDTH = 240;

const StyledLayout = styled.div`
  display: grid;
  grid-template-columns: ${LEFT_COLUMN_WIDTH}px 1fr;
  gap: ${({ theme }) => theme.spacing.medium};
  align-items: start;
`;

const StyledLeftColumn = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`;

const StyledRightContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
  min-width: 0;
`;

const StyledGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.default};
`;

const StyledPageBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 24px;
`;

const SettingsPageBanner = styled.div`
  padding: ${({ theme }) => theme.spacing.small} 0;

  h4 {
    font-size: 11px;
    font-weight: 700;
    color: #111827;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  p {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }
`;

const StyledPageBannerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledPageBannerIconWrap = styled.div`
  flex-shrink: 0;
  color: #1e40af;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledPageBannerTitle = styled.div`
  font-size: 14px;
  color: #1e40af;
  font-weight: 600;
`;

const StyledPageBannerSubtitle = styled.div`
  font-size: 13px;
  color: #1d4ed8;
  margin: 0;
`;

const EMPTY_SCHEMA: JSONSchema = { type: 'object', properties: {}, required: [] };

interface MicrositePageProps {
  projectData?: any;
  grants?: any;
  isLoading: boolean;
  onAction: any;
}

export const MicrositePage: FC<MicrositePageProps> = ({ projectData, grants, isLoading: isParentLoading }) => {
  const dispatch = useDispatch();
  const { setLongPullItemId } = useWorkspace();

  const { data: workspaceTemplateData, isLoading: isTemplateLoading } = useGetProjectTemplateByIdQuery(
    { workspaceId: projectData?.ProjectTemplate?.ID },
    { skip: !projectData?.ProjectTemplate?.ID },
  );

  const [updateDocument, { isLoading: isUpdateDocumentLoading }] = useUpdateDocumentMutation();
  const [updateProject, { isLoading: isUpdateProjectLoading }] = useUpdateProjectMutation();

  const isLoading = isParentLoading || isTemplateLoading || isUpdateDocumentLoading || isUpdateProjectLoading;

  const [isAddPageDialogOpen, setIsAddPageDialogOpen] = useState(false);
  const [optimisticPagesConfig, setOptimisticPagesConfig] = useState<MicrositePageInstance[] | null>(null);

  const htmlDocument = useMemo(
    () => projectData?.Documents?.find((d: any) => d.DocumentTemplate?.OutputType === 'html'),
    [projectData],
  );

  const serverPagesConfig: MicrositePageInstance[] | null = useDeepCompareMemo(() => {
    const raw = htmlDocument?.Values?.__pagesConfig;
    return Array.isArray(raw) ? raw : null;
  }, [htmlDocument?.Values]);

  const htmlTemplate = useDeepCompareMemo(() => {
    return workspaceTemplateData?.DocumentTemplates?.find((t: any) => t.OutputType === 'html');
  }, [workspaceTemplateData]);

  const templatePages: TemplatePage[] = useDeepCompareMemo(() => {
    return htmlTemplate?.Template?.pages ?? [];
  }, [htmlTemplate]);

  // dynamic mode when __pagesConfig has been saved, OR when the template defines page types
  // (new workspaces start with empty pages list instead of legacy static tabs)
  const isDynamic = serverPagesConfig !== null || templatePages.length > 0;
  const pagesConfig: MicrositePageInstance[] = optimisticPagesConfig ?? serverPagesConfig ?? [];

  const tabs: MicrositePagesRailTab[] = useDeepCompareMemo(() => {
    if (!workspaceTemplateData) return [];

    if (isDynamic) {
      const settingsTab: MicrositePagesRailTab = { label: 'Workspace Settings', value: 'default' };
      const instanceTabs: MicrositePagesRailTab[] = pagesConfig.map((instance) => ({
        label: instance.displayName,
        value: instance.instanceKey,
        isDynamic: true,
      }));

      if (workspaceTemplateData?.ValuesSchema?.properties) {
        return [settingsTab, ...instanceTabs];
      }
      return instanceTabs;
    }

    // Legacy mode: static template pages
    const pages: any[] = htmlTemplate?.Template?.pages ?? [];
    if (pages.length === 0) return [];

    const pagesTabs = pages.map((page: any) => ({
      label: page?.displayName || page?.name || 'Unknown Page',
      value: page.name,
    }));

    if (workspaceTemplateData?.ValuesSchema?.properties) {
      return [{ label: 'Workspace Settings', value: 'default' }, ...pagesTabs];
    }

    return pagesTabs;
  }, [workspaceTemplateData, isDynamic, pagesConfig, htmlTemplate]);

  const rawSchemas: Record<string, { schema: JSONSchema; initialValues: any; query?: string }> =
    useDeepCompareMemo(() => {
      const projectValues = projectData?.Values?.find((item: any) => item?.name === 'project');
      const query = htmlDocument?.DocumentTemplate?.Query?.Query || '';

      const settingsSchema = {
        schema: projectData?.ProjectTemplate?.ValuesSchema ?? EMPTY_SCHEMA,
        initialValues: projectValues?.data ?? {},
      };

      if (isDynamic) {
        const schemas: Record<string, { schema: JSONSchema; initialValues: any; query?: string }> = {
          default: settingsSchema,
        };

        for (const instance of pagesConfig) {
          const schema =
            (htmlTemplate?.ValuesSchema?.properties?.[instance.templatePageName] as JSONSchema) ?? EMPTY_SCHEMA;
          const initialValues = htmlDocument?.Values?.[instance.instanceKey] ?? {};
          schemas[instance.instanceKey] = { schema, initialValues, query };
        }

        return schemas;
      }

      // Legacy mode
      const htmlData = projectData?.Values?.find((item: any) => item?.name === 'html');

      if (htmlTemplate) {
        const htmlSchemas = Object.entries(htmlTemplate.ValuesSchema?.properties ?? {}).reduce(
          (acc, [page, schema]) => ({
            ...acc,
            [page]: { schema: schema as JSONSchema, initialValues: htmlData?.data?.[page] ?? {}, query },
          }),
          { default: { schema: EMPTY_SCHEMA, initialValues: {} } } as Record<string, any>,
        );

        htmlSchemas['default'] = settingsSchema;
        return htmlSchemas;
      }

      return { default: settingsSchema };
    }, [workspaceTemplateData, projectData, isDynamic, pagesConfig, htmlTemplate, htmlDocument]);

  const tabbedFormTabs = useMemo(
    () =>
      tabs.map((tab) => ({
        name: tab.value,
        schema: rawSchemas[tab.value]?.schema ?? null,
        initialValues: rawSchemas[tab.value]?.initialValues ?? {},
      })),
    [tabs, rawSchemas],
  );

  const { activeTab, tabReadiness, handleTabChange, setActiveTab, handleSubmit, onDirtyChange, onValidChange } =
    useTabbedForm({
      tabs: tabbedFormTabs,
      onSave: async (tabName, data) => {
        if (!projectData || !htmlDocument) return;

        if (tabName === 'default') {
          try {
            await updateProject({ projectId: projectData.ID, data: { values: data } }).unwrap();
          } catch (error) {
            dispatch(addNotification({ type: 'error', message: 'Workspace update failed. Please try again.' }));
            captureError(error, { message: 'Failed to update workspace.', component: 'MicrositePage' });
            throw new Error('save failed');
          }
        } else {
          const values: Record<string, any> = { ...htmlDocument.Values, [tabName]: data };
          const updateResult: any = await updateDocument({
            documentId: htmlDocument.ID,
            projectId: projectData.ID,
            data: { values },
          });
          if (updateResult?.data?.result !== 'success') {
            dispatch(addNotification({ type: 'error', message: 'Couldn\'t save document changes. Please try again.' }));
            throw new Error('save failed');
          }
        }

        dispatch(addNotification({ type: 'success', message: 'Project updated successfully.' }));
        setLongPullItemId(null);
      },
      autoSave: false,
    });

  const currentTabData = activeTab ? rawSchemas[activeTab] : rawSchemas['default'];

  const previewLink = htmlDocument
    ? `${config.assetServiceBaseUrl}/microsites/preview?slug=/${projectData?.ID}/${htmlDocument.ID}`
    : undefined;

  const activePageLabel = useMemo(() => {
    if (!activeTab || activeTab === 'default') return null;
    return tabs.find((t) => t.value === activeTab)?.label ?? null;
  }, [activeTab, tabs]);

  const isEtlEnabled = !!projectData?.ProjectTemplate?.HasEtl || !!projectData?.HasEtl;
  const canUploadWorkbook = hasAccessByRole({ allowedRoles: [aclRolesMap.DATA_ADMIN], role: grants?.role });
  const showWorkbookUpload = isEtlEnabled && canUploadWorkbook;

  const { data: uploadedDataFolderAssets, refetch: refetchUploadedDataFolderAssets } = useGetFolderAssetsQuery(
    projectData?.UploadedDataFolderID ? { parentId: projectData.UploadedDataFolderID } : skipToken,
  );
  const uploadedWorkbook = uploadedDataFolderAssets?.[0] ?? null;

  const handleWorkbookUploadComplete = () => {
    refetchUploadedDataFolderAssets();
    ProjectWorkbookUploadEventBus.publish('@clatter/PROJECT_WORKBOOK_UPLOAD_COMPLETED', undefined);
  };

  const queryGqlData = async (query: string) => {
    const response = await fetch(`${config.assetServiceBaseUrl}/v2/projects/${projectData?.ID}/graphql`, {
      method: 'POST',
      body: JSON.stringify({ query }),
      headers: { Authorization: `Bearer ${getLocalStorageAccessToken()}` },
    });
    return await response.json();
  };

  const savePagesConfig = async (newConfig: MicrositePageInstance[]) => {
    if (!htmlDocument) return;
    setOptimisticPagesConfig(newConfig);
    const values = { ...htmlDocument.Values, __pagesConfig: newConfig };
    const result: any = await updateDocument({
      documentId: htmlDocument.ID,
      projectId: projectData.ID,
      data: { values },
    });
    if (result?.data?.result !== 'success') {
      setOptimisticPagesConfig(null);
      dispatch(addNotification({ type: 'error', message: 'Failed to save pages configuration.' }));
    }
  };

  const handleAddPageDialogOpen = () => {
    // blur focused element before opening dialog to avoid "aria-hidden on focused element" warning
    // uncomment if it still shows in production
    // (document.activeElement as HTMLElement | null)?.blur?.();
    setIsAddPageDialogOpen(true);
  };

  const handleAddPage = async (templatePage: TemplatePage) => {
    const newInstanceKey = generateInstanceKey(templatePage.name, pagesConfig);
    const baseDisplayName = templatePage.displayName || templatePage.name;
    const newDisplayName = generateUniqueDisplayName(baseDisplayName, pagesConfig);
    const newSlug = generateUniqueSlug(newDisplayName, pagesConfig);

    const newInstance: MicrositePageInstance = {
      instanceKey: newInstanceKey,
      templatePageName: templatePage.name,
      displayName: newDisplayName,
      slug: newSlug,
    };

    const newConfig = [...pagesConfig, newInstance];

    // Initialize __pagesConfig if this is the first dynamic page (transition from legacy → dynamic)
    await savePagesConfig(newConfig);
    setActiveTab(newInstanceKey); // direct switch — no auto-save
  };

  const handleRenamePage = async (instanceKey: string, newDisplayName: string) => {
    const otherInstances = pagesConfig.filter((p) => p.instanceKey !== instanceKey);
    const newSlug = generateUniqueSlug(newDisplayName, otherInstances);

    const newConfig = pagesConfig.map((p) =>
      p.instanceKey === instanceKey ? { ...p, displayName: newDisplayName, slug: newSlug } : p,
    );

    await savePagesConfig(newConfig);
  };

  const handleDeletePage = async (instanceKey: string) => {
    const newConfig = pagesConfig.filter((page) => page.instanceKey !== instanceKey);

    await savePagesConfig(newConfig);

    if (activeTab === instanceKey) {
      const remainingTabs = tabs.filter((tab) => tab.value !== instanceKey);
      if (remainingTabs[0]) setActiveTab(remainingTabs[0].value);
    }
  };

  const handleReorderPages = async (newOrder: string[]) => {
    const newConfig = newOrder
      .map((instanceKey) => pagesConfig.find((page) => page.instanceKey === instanceKey))
      .filter(Boolean) as MicrositePageInstance[];

    await savePagesConfig(newConfig);
  };

  const getTabStatusForRail = useCallback(
    (tabValue: string): MicrositeTabStatus => {
      if (!rawSchemas[tabValue]) return 'empty';
      if (tabValue === 'default' && showWorkbookUpload && !uploadedWorkbook) return 'invalid';
      return tabReadiness[tabValue] ? 'valid' : 'invalid';
    },
    [rawSchemas, tabReadiness, showWorkbookUpload, uploadedWorkbook],
  );

  const isPublishEnabled = useMemo(() => {
    if (pagesConfig.length === 0) return false;
    return tabs.every((tab) => getTabStatusForRail(tab.value) === 'valid');
  }, [pagesConfig, tabs, getTabStatusForRail]);

  return (
    <>
      <StyledLayout>
        <StyledLeftColumn>
          <StickyHeader top={STICKY_TOP} noWrapper>
            <MicrositePagesRail
              tabs={tabs.map((tab) => ({ ...tab, status: getTabStatusForRail(tab.value) }))}
              activeTab={activeTab ?? ''}
              previewLink={previewLink}
              footer={
                <MicrositesPublishSection
                  projectData={projectData}
                  isQueryValid={isPublishEnabled}
                  isQueriesEnabled={false}
                  isLoading={isLoading}
                  buttonOnly
                  panelAnchor="left"
                />
              }
              onTabChange={handleTabChange}
              onAddPage={handleAddPageDialogOpen}
              onRenamePage={isDynamic ? handleRenamePage : undefined}
              onDeletePage={isDynamic ? handleDeletePage : undefined}
              onReorderPages={isDynamic ? handleReorderPages : undefined}
            />
          </StickyHeader>
        </StyledLeftColumn>

        <StyledRightContent>
          {currentTabData && (
            <NgCard>
              <NgCardContent>
                <StyledGrid>
                  {activePageLabel && (
                    <StyledPageBanner>
                      <StyledPageBannerRow>
                        <StyledPageBannerIconWrap>
                          <PageDocumentIcon />
                        </StyledPageBannerIconWrap>
                        <StyledPageBannerTitle>{activePageLabel}</StyledPageBannerTitle>
                      </StyledPageBannerRow>
                      <StyledPageBannerSubtitle>Configure settings for this page</StyledPageBannerSubtitle>
                    </StyledPageBanner>
                  )}

                  {showWorkbookUpload && activeTab === 'default' && (
                    <>
                      <SettingsPageBanner>
                        <h4>Marketing Website Settings</h4>
                        <p>Configure global settings for your website</p>
                      </SettingsPageBanner>

                      <StepStatus label="Upload Data Source" ready={!!uploadedWorkbook} large />

                      <WorkbookUpload
                        project={projectData}
                        uploadFolderId={projectData?.UploadedDataFolderID}
                        currentAsset={uploadedWorkbook}
                        onComplete={handleWorkbookUploadComplete}
                      />
                    </>
                  )}
                  <SchemaForm
                    key={activeTab}
                    schema={currentTabData.schema}
                    initialValues={currentTabData.initialValues}
                    isLoading={isLoading}
                    query={currentTabData.query}
                    fieldKeyPrefix={activeTab ?? ''}
                    queryGqlData={queryGqlData}
                    onSubmit={handleSubmit}
                    onDirtyChange={onDirtyChange}
                    onValidChange={onValidChange}
                  />
                </StyledGrid>
              </NgCardContent>
            </NgCard>
          )}
        </StyledRightContent>
      </StyledLayout>

      <AddPageDialog
        open={isAddPageDialogOpen}
        templatePages={templatePages}
        onSelect={handleAddPage}
        onClose={() => setIsAddPageDialogOpen(false)}
      />
    </>
  );
};
